// Hooks for dedicated IP management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const REFETCH_MS = 15_000

interface IpListRow {
  id: string
  account_id: string
  ip_address: string
  rdns: string | null
  status: string
  warming_day: number
  daily_sent: number
  daily_limit: number
  pause_reason: string | null
  warming_started_at: string | null
  warming_paused_at: string | null
  last_health_check: string | null
  created_at: string
  today_sent: number | null
  today_bounce_rate: string | null
}

interface IpListResponse {
  ips: IpListRow[]
}

export type { IpListResponse, IpListRow }

export function useIps() {
  return useQuery<IpListResponse>({
    queryKey: ['ips'],
    queryFn: () => apiClient.get(endpoints.ips.list),
    refetchInterval: REFETCH_MS,
  })
}

export function usePauseIp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post(endpoints.ips.pause(id), { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ips'] })
    },
  })
}

export function useResumeIp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post(endpoints.ips.resume(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ips'] })
    },
  })
}

export function useResetIp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post(endpoints.ips.reset(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ips'] })
    },
  })
}

export function useAdvanceIp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post(endpoints.ips.advance(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ips'] })
    },
  })
}
